// pages/VisitDetail.jsx - Complete with conditional fields and light/dark mode
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Building2,
  User,
  Calendar,
  Clock,
  MapPin,
  Wrench,
  TrendingUp,
  CheckCircle,
  XCircle,
  Save,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  X,
  Phone,
  Mail,
  Briefcase,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const VisitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const prevPhoto = () =>
    setLightbox((prev) => ({
      ...prev,
      index:
        (prev.index - 1 + (visit.attachments?.length || 1)) %
        (visit.attachments?.length || 1),
    }));
  const nextPhoto = () =>
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index + 1) % (visit.attachments?.length || 1),
    }));

  const [expandedSections, setExpandedSections] = useState({
    visitInfo: true,
    workInfo: true,
    salesInfo: true,
    location: true,
    checklist: true,
  });

  useEffect(() => {
    fetchVisit();
  }, [id]);

  const fetchVisit = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVisit(id);
      setVisit(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching visit:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await apiService.updateVisit(id, formData);
      setVisit(formData);
      setEditMode(false);
    } catch (error) {
      console.error("Error updating visit:", error);
      alert("Failed to update visit");
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this visit? This action cannot be undone.",
      )
    ) {
      try {
        await apiService.deleteVisit(id);
        navigate("/visits");
      } catch (error) {
        console.error("Error deleting visit:", error);
        alert("Failed to delete visit");
      }
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-green-500/20 text-green-400 border-green-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
      no_show: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };
    return colors[status] || colors.scheduled;
  };

  const getVisitTypeColor = (visitType) => {
    const colors = {
      technical: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      sales: "bg-green-500/20 text-green-400 border-green-500/30",
    };
    return colors[visitType] || colors.technical;
  };

  const getVisitTypeIcon = (visitType) => {
    if (visitType === "technical") {
      return <Wrench className="w-5 h-5" />;
    }
    return <TrendingUp className="w-5 h-5" />;
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Loading visit...
        </div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Visit not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <button
                onClick={() => navigate(-1)}
                className={`mt-1 flex-shrink-0 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1
                  className={`text-xl sm:text-2xl font-bold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {visit.visitName}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1 ${getVisitTypeColor(
                      visit.visitType,
                    )}`}
                  >
                    {getVisitTypeIcon(visit.visitType)}
                    {visit.visitType === "technical" ? "Technical" : "Sales"}
                  </span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                      visit.status,
                    )}`}
                  >
                    {visit.status.replace("_", " ").toUpperCase()}
                  </span>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {visit.account?.name ?? visit.opportunity?.name}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 pl-8 sm:pl-0">
              {editMode ? (
                <>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setFormData(visit);
                    }}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Save className="w-4 h-4" />
                    <span className="hidden sm:inline">Save Changes</span>
                    <span className="sm:hidden">Save</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setEditMode(true)}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                  >
                    <Edit className="w-4 h-4" />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {/* Visit Information */}
          <div
            className={`rounded-lg overflow-hidden border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <button
              onClick={() => toggleSection("visitInfo")}
              className={`w-full px-4 sm:px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
            >
              <h2
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Visit Information
              </h2>
              {expandedSections.visitInfo ? (
                <ChevronDown
                  className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                />
              ) : (
                <ChevronRight
                  className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                />
              )}
            </button>

            {expandedSections.visitInfo && (
              <div className="px-4 sm:px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Account */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Account
                  </label>
                  <div
                    className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Building2
                      className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    {visit.account?.name || "—"}
                  </div>
                </div>

                {/* Contact */}
                {visit.contact && (
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Contact
                    </label>
                    <div
                      className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <User
                        className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      />
                      {visit.contact.firstName} {visit.contact.lastName}
                    </div>
                  </div>
                )}

                {/* Visit Type */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Visit Type
                  </label>
                  <div className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {visit.visitType === "technical" ? "Technical" : "Sales"}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Status
                  </label>
                  {editMode ? (
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                      className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="no_show">No Show</option>
                    </select>
                  ) : (
                    <div
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {visit.status.replace("_", " ").toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Visit Date */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Visit Date
                  </label>
                  <div
                    className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Calendar
                      className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    {formatDate(visit.visitDate)}
                  </div>
                </div>

                {/* Visit Date & Time */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Visit Date & Time
                  </label>
                  <div
                    className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Clock
                      className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    {formatDateTime(visit.visitDateTime)}
                  </div>
                </div>

                {/* Owner */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Owner
                  </label>
                  <div
                    className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <User
                      className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    {visit.owner?.name || "—"}
                  </div>
                </div>

                {/* Assigned Engineer/Sales Member */}
                {visit.visitType === "technical" ? (
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Engineer
                    </label>
                    <div
                      className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <User
                        className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      />
                      {visit.engineer?.name || "—"}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Sales Member
                    </label>
                    <div
                      className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <User
                        className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      />
                      {visit.salesMember?.name || "—"}
                    </div>
                  </div>
                )}

                {/* Created Date */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Created Date
                  </label>
                  <div
                    className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Calendar
                      className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    {formatDateTime(visit.createdAt)}
                  </div>
                </div>

                {/* Last Modified */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Last Modified
                  </label>
                  <div
                    className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Clock
                      className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    {formatDateTime(visit.updatedAt)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* TECHNICAL VISIT SECTIONS */}
          {visit.visitType === "technical" && (
            <>
              {/* Work Information */}
              <div
                className={`rounded-lg overflow-hidden border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <button
                  onClick={() => toggleSection("workInfo")}
                  className={`w-full px-4 sm:px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
                >
                  <h2
                    className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Work Information
                  </h2>
                  {expandedSections.workInfo ? (
                    <ChevronDown
                      className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  ) : (
                    <ChevronRight
                      className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  )}
                </button>

                {expandedSections.workInfo && (
                  <div className="px-4 sm:px-6 pb-6 space-y-4">
                    {/* Job Details */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Job Details
                      </label>
                      {editMode ? (
                        <textarea
                          rows="3"
                          value={formData.jobDetails || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              jobDetails: e.target.value,
                            })
                          }
                          className={`w-full px-3 py-2 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                        />
                      ) : (
                        <div
                          className={`whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {visit.jobDetails || "—"}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {/* Router IP Address */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Router IP Address
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            value={formData.routerIpAddress || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                routerIpAddress: e.target.value,
                              })
                            }
                            className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                          />
                        ) : (
                          <div
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {visit.routerIpAddress || "—"}
                          </div>
                        )}
                      </div>

                      {/* Router Password */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Router Password
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            value={formData.routerPassword || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                routerPassword: e.target.value,
                              })
                            }
                            className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                          />
                        ) : (
                          <div
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {visit.routerPassword || "—"}
                          </div>
                        )}
                      </div>

                      {/* Router Type */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Router Type
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            value={formData.routerType || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                routerType: e.target.value,
                              })
                            }
                            className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                          />
                        ) : (
                          <div
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {visit.routerType || "—"}
                          </div>
                        )}
                      </div>

                      {/* Broadband Type */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Broadband Type
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            value={formData.broadbandType || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                broadbandType: e.target.value,
                              })
                            }
                            className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                          />
                        ) : (
                          <div
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {visit.broadbandType || "—"}
                          </div>
                        )}
                      </div>

                      {/* Broadband Speed */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Broadband Speed
                        </label>
                        {editMode ? (
                          <input
                            type="text"
                            value={formData.broadbandSpeed || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                broadbandSpeed: e.target.value,
                              })
                            }
                            className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                          />
                        ) : (
                          <div
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {visit.broadbandSpeed || "—"}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Outstanding Work */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Outstanding Work
                      </label>
                      {editMode ? (
                        <textarea
                          rows="3"
                          value={formData.outstandingWork || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              outstandingWork: e.target.value,
                            })
                          }
                          className={`w-full px-3 py-2 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                        />
                      ) : (
                        <div
                          className={`whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {visit.outstandingWork || "—"}
                        </div>
                      )}
                    </div>

                    {/* Work Notes */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Notes
                      </label>
                      {editMode ? (
                        <textarea
                          rows="3"
                          value={formData.workNotes || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              workNotes: e.target.value,
                            })
                          }
                          className={`w-full px-3 py-2 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                        />
                      ) : (
                        <div
                          className={`whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {visit.workNotes || "—"}
                        </div>
                      )}
                    </div>

                    {/* Call Recording Notes */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Call Recording Notes
                      </label>
                      {editMode ? (
                        <textarea
                          rows="2"
                          value={formData.callRecordingNotes || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              callRecordingNotes: e.target.value,
                            })
                          }
                          className={`w-full px-3 py-2 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                        />
                      ) : (
                        <div
                          className={`whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {visit.callRecordingNotes || "—"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Installation Checklist */}
              <div
                className={`rounded-lg overflow-hidden border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <button
                  onClick={() => toggleSection("checklist")}
                  className={`w-full px-4 sm:px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
                >
                  <h2
                    className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Installation Checklist
                  </h2>
                  {expandedSections.checklist ? (
                    <ChevronDown
                      className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  ) : (
                    <ChevronRight
                      className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  )}
                </button>

                {expandedSections.checklist && (
                  <div className="px-4 sm:px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      {visit.callSwitchCommunicatorAppInstalled ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        Call Switch Communicator App Installed
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {visit.fullTrainingProvided ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        Full Training Provided
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {visit.allHardwareInstalled ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        All Hardware Installed
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {visit.existingPhoneEquipmentLeft ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        Existing Phone Equipment Left
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {visit.divertApplied ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        Divert Applied
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {visit.callRecordingRegistered ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        Call Recording Registered
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Photos */}
              {visit.attachments?.length > 0 && (
                <div
                  className={`rounded-lg overflow-hidden border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                >
                  <div className="px-4 sm:px-6 py-4">
                    <h2
                      className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Photos
                    </h2>
                  </div>
                  <div className="px-4 sm:px-6 pb-6 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                    {visit.attachments.map((photo, index) => (
                      <button
                        key={photo.id}
                        onClick={() => openLightbox(index)}
                        className="focus:outline-none"
                      >
                        <img
                          src={photo.fileUrl}
                          alt={photo.fileName}
                          className={`rounded-lg w-full h-40 object-cover transition-all cursor-pointer ${isDarkMode ? "border border-gray-700 hover:border-blue-500 hover:opacity-80" : "border border-gray-200 hover:border-blue-400 hover:opacity-80"}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* SALES VISIT SECTIONS */}
          {visit.visitType === "sales" && (
            <div
              className={`rounded-lg overflow-hidden border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <button
                onClick={() => toggleSection("salesInfo")}
                className={`w-full px-4 sm:px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
              >
                <h2
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Sales Visit Details
                </h2>
                {expandedSections.salesInfo ? (
                  <ChevronDown
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                ) : (
                  <ChevronRight
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                )}
              </button>

              {expandedSections.salesInfo && (
                <div className="px-4 sm:px-6 pb-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {/* Opportunity */}
                    {visit.opportunity && (
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Opportunity
                        </label>
                        <div
                          className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          <Briefcase
                            className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          />
                          {visit.opportunity.name}
                        </div>
                      </div>
                    )}

                    {/* Outcome */}
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Outcome
                      </label>
                      {editMode ? (
                        <select
                          value={formData.outcome || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              outcome: e.target.value,
                            })
                          }
                          className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                        >
                          <option value="">--None--</option>
                          <option value="QUAL">QUAL</option>
                          <option value="NON QUAL">NON QUAL</option>
                          <option value="SAT">SAT</option>
                          <option value="NON SAT">NON SAT</option>
                          <option value="SOLD">SOLD</option>
                          <option value="NOT INTERESTED">NOT INTERESTED</option>
                        </select>
                      ) : (
                        <div
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {visit.outcome || "—"}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Visit Notes */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Visit Notes
                    </label>
                    {editMode ? (
                      <textarea
                        rows="4"
                        value={formData.vsistNotes || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            vsistNotes: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {visit.vsistNotes || "—"}
                      </div>
                    )}
                  </div>

                  {/* Customer Signer */}
                  {visit.customerSigner && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Customer Signer
                        </label>
                        <div
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {visit.customerSigner}
                        </div>
                      </div>

                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Customer Signer Email
                        </label>
                        <div
                          className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          <Mail
                            className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          />
                          {visit.customerSignerEmail || "—"}
                        </div>
                      </div>

                      {visit.signedDateTime && (
                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Signed Date/Time
                          </label>
                          <div
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <Clock
                              className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            />
                            {formatDateTime(visit.signedDateTime)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Location Information (Common) */}
          <div
            className={`rounded-lg overflow-hidden border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <button
              onClick={() => toggleSection("location")}
              className={`w-full px-4 sm:px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
            >
              <h2
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Location Information
              </h2>
              {expandedSections.location ? (
                <ChevronDown
                  className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                />
              ) : (
                <ChevronRight
                  className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                />
              )}
            </button>

            {expandedSections.location && (
              <div className="px-4 sm:px-6 pb-6 space-y-4">
                {/* Address */}
                {(visit.street ||
                  visit.city ||
                  visit.state ||
                  visit.postalCode ||
                  visit.country) && (
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Address
                    </label>
                    <div
                      className={`flex items-start gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <MapPin
                        className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"} mt-1`}
                      />
                      <div>
                        {visit.street && <div>{visit.street}</div>}
                        <div>
                          {[visit.city, visit.state, visit.postalCode]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                        {visit.country && <div>{visit.country}</div>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Location Coordinates */}
                {visit.locationLat && visit.locationLng && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Latitude
                      </label>
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {visit.locationLat}
                      </div>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Longitude
                      </label>
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {visit.locationLng}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && visit.attachments?.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
            {lightbox.index + 1} / {visit.attachments.length}
          </div>

          {/* Prev button */}
          {visit.attachments.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevPhoto();
              }}
              className="absolute left-4 text-white hover:text-gray-300 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-all"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Image */}
          <img
            src={visit.attachments[lightbox.index].fileUrl}
            alt={visit.attachments[lightbox.index].fileName}
            className="max-h-[85vh] max-w-[85vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          {visit.attachments.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextPhoto();
              }}
              className="absolute right-4 text-white hover:text-gray-300 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-all"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* Filename */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-gray-300 text-sm bg-black/50 px-3 py-1 rounded-full">
            {visit.attachments[lightbox.index].fileName}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitDetail;
